import { cardPeriodForDate } from './finance';
import { isRecordedActivity } from './bank-posting';
import { paymentTimingForCut } from './card-payment-timing';
import type { FinancialSnapshot, Movement } from './types';

export interface CardBalance {
  balanceCents: number;
  unpaidCuts: Array<{ period: string; amountCents: number; coveredCents: number }>;
}
/** Vista de deuda registrada, independiente del dinero apartado y del presupuesto. */
export function cardBalance(snapshot: Pick<FinancialSnapshot, 'cardPayments' | 'bankCredits' | 'cardPaymentTimingAdjustments'>, movements: Movement[], events: Array<Record<string, unknown>>, card: string, cutDay: number, today: string): CardBalance {
  const paid = new Set(events.filter(e => String(e.card ?? e.Tarjeta ?? '') === card).map(e => String(e.period ?? e.closedPeriod ?? e['Periodo cerrado'] ?? '')));
  const rows = [...new Map(movements.map(m => [m.id, m])).values()].filter(m => m.paymentMethod === card);
  const periodFor = (m: Movement) => m.cardPeriod ?? cardPeriodForDate(String(m.spentOn ?? m.createdAt).slice(0, 10), cutDay) ?? '';
  // Conserva también el cierre marcado en los movimientos cuando el evento no está en la copia local.
  for (const period of new Set(rows.filter(m => m.kind === 'expense').map(periodFor))) {
    const same = rows.filter(m => m.kind === 'expense' && periodFor(m) === period);
    if (same.length && same.every(m => m.cardPaymentStatus === 'paid' || m.historicalSettlement)) paid.add(period);
  }
  const cuts = new Map<string, { amountCents: number; coveredCents: number }>();
  const ensure = (period: string) => { if (!cuts.has(period)) cuts.set(period, { amountCents: 0, coveredCents: 0 }); return cuts.get(period)!; };
  for (const m of rows) {
    const period = periodFor(m);
    if (paid.has(period) || m.cardPaymentStatus === 'paid' || m.historicalSettlement || !isRecordedActivity(m, today)) continue;
    const cut = ensure(period);
    cut.amountCents += m.billedAmountCents ?? m.amountCents;
    cut.coveredCents += Math.max(0, m.coveredAmountCents ?? 0);
  }
  const inherited = snapshot.cardPayments.find(c => c.name === card);
  if (inherited?.unassignedPeriod && !paid.has(inherited.unassignedPeriod) && inherited.unassignedPeriod.slice(0, 10) <= today) {
    const amount = Math.max(0, inherited.unassignedCents ?? 0);
    if (amount) { const cut = ensure(inherited.unassignedPeriod); cut.amountCents += amount; cut.coveredCents += amount; }
  }
  for (const c of snapshot.bankCredits ?? []) {
    if (c.card === card && !paid.has(c.period) && c.postedOn <= today && c.period.slice(0, 10) <= today) ensure(c.period).amountCents -= c.amountCents;
  }
  for (const a of snapshot.cardPaymentTimingAdjustments ?? []) {
    if (a.card !== card) continue;
    for (const period of [a.paymentPeriod, a.sourcePeriod]) if (!paid.has(period) && period.slice(0, 10) <= today) ensure(period);
  }
  for (const [period, cut] of cuts) cut.amountCents += paymentTimingForCut(snapshot.cardPaymentTimingAdjustments ?? [], card, period);
  const unpaidCuts = [...cuts].map(([period, c]) => ({ period, ...c })).sort((a, b) => a.period.localeCompare(b.period));
  return { balanceCents: unpaidCuts.reduce((n, c) => n + c.amountCents, 0), unpaidCuts };
}
